import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import axios from 'axios';
import {recordingConfirmPopupDisplay} from '../../../../../../../actions/popupActions';
import {flash} from '../../../../../../../actions/sharingDetailsActions';
import Button from '../../../../../button';
import {API_ROOT} from '../../../../../../utils';

function AudioRecorder ({action, onClose}) {

	const [isRecording, setIsRecording] = useState(false);
	//const [audioChunks, setAudioChunks] = useState([]);
	const [recordingTime, setRecordingTime] = useState(0);
	const [recordSubmitted, setRecordSubmitted] = useState(false);

	const mediaRecorderRef = useRef(null);
	const intervalRef = useRef(null);
	const canvasRef = useRef(null);
	const animationIdRef = useRef(null);
	const audioChunksRef = useRef([]);

	const MAX_DURATION = 120;
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const selectedContactsGroup = useSelector(state => state.contactsGroupItemReducer.getSelectedContactsGroup);
	const profile = useSelector(state => state.profileReducer.getProfile);
	const contactDetails = useSelector(state => state.contactDetailsReducer.findContactDetails);

	const handleContactOperation = useCallback(async (payload, fileRefId) => {
		try {
			if(action === 'edit'){
				if(contactDetails){
					await axios.put(`${API_ROOT}/api/v2/contact/audio/${contactDetails.id}`, payload, {
						withCredentials: true
					});
				}
			} else {
				await axios.post(`${API_ROOT}/api/v2/contact/audio`, payload, {
					withCredentials: true
				});
			}

			setTimeout(() => {
				if(action === 'edit'){
					dispatch(recordingConfirmPopupDisplay({ mode: 'edit', fileRefId }));
				} else {
					dispatch(recordingConfirmPopupDisplay({ mode: 'create', fileRefId }));
				}
			}, 500);
		} catch (error) {
			dispatch(flash({ type: 'danger', message: error.message }));
		}
	}, [dispatch, action, contactDetails]);

	const uploadAudio = useCallback(async (blob) => {
		const formData = new FormData();
		formData.append('operation', action === 'edit' ? 'UPDATE_CONTACT_FROM_AUDIO' : 'CREATE_CONTACT_FROM_AUDIO');
		formData.append('fileType', 'AUDIO');
		let ext = 'mp3';
		if (blob && blob.type) {
			if (blob.type.includes('webm')) ext = 'webm';
			else if (blob.type.includes('ogg')) ext = 'ogg';
			else if (blob.type.includes('wav')) ext = 'wav';
			else if (blob.type.includes('mp4') || blob.type.includes('m4a')) ext = 'm4a';
		}
		formData.append("file", blob, `recording_${Date.now()}.${ext}`);

		try {
			const res = await axios.post(`${API_ROOT}/api/v1/file?source=WEBAPP`, formData, {
				headers: { "Content-Type": "multipart/form-data" },
				withCredentials: true
			});

			console.log("Réponse API:", res.data);

			if(action === 'edit'){
				if(contactDetails){
					await handleContactOperation({
						labelId: selectedContactsGroup ? selectedContactsGroup.id : '6',
						labelOwnerId: selectedContactsGroup ? selectedContactsGroup.ownerId : profile.id,
						audioFileRefId: res.data.fileId,
						phoneContactEntry: contactDetails.phoneContactEntry
					}, res.data.fileId);
				}
			} else {
				await handleContactOperation({
					labelId: selectedContactsGroup ? selectedContactsGroup.id : '6',
					labelOwnerId: selectedContactsGroup ? selectedContactsGroup.ownerId : profile.id,
					fileRefId: res.data.fileId,
				}, res.data.fileId);
			}

		} catch (err) {
			console.error("Upload échoué:", err);
			const errorMsg = err.response && err.response.data && err.response.data.message
				? err.response.data.message
				: err.message;
			dispatch(flash({ type: 'danger', message: errorMsg }));
		}
	}, [dispatch, action, contactDetails, handleContactOperation, profile, selectedContactsGroup]);

	const stopRecording = useCallback(() => {
		const recorder = mediaRecorderRef.current;
		if (!recorder) return;

		if (recorder.state === "recording") {
			console.info(audioChunksRef.current);
			recorder.stop();
			setIsRecording(false);
		}

		if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
		if (intervalRef.current) clearInterval(intervalRef.current);
	}, []);

	const startRecording = useCallback(async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			const recorder = new MediaRecorder(stream);
			mediaRecorderRef.current = recorder;

			recorder.ondataavailable = (event) => {
				if (event.data.size > 0) {
					audioChunksRef.current.push(event.data);
					//setAudioChunks(prev => [...prev, event.data]);
				}
			};

			recorder.start();
			setIsRecording(true);

			intervalRef.current = setInterval(() => {
				setRecordingTime(t => {
					if (t + 1 >= MAX_DURATION) {
						stopRecording();
					}
					return t + 1;
				});
			}, 1000);

			visualize(stream);
		} catch (err) {
			console.error("Micro refusé:", err);
			setIsRecording(false);
		}
	}, [stopRecording]);

	const saveRecording = useCallback(() => {
		setRecordSubmitted(true);

		const recorder = mediaRecorderRef.current;
		if (!recorder) return;

		const finalize = async () => {

			if (!audioChunksRef.current.length) {
				console.warn("No chunk audio saved!");
				return;
			}

			const blob = new Blob(audioChunksRef.current, { type: "audio/mpeg" });
			console.log("Enregistrement terminé:", blob);

			await uploadAudio(blob);
			audioChunksRef.current = [];
			//dispatch(isAudioRecorderPopupShown(false));
			onClose()
		};

		if (isRecording && recorder.state === "recording") {
			recorder.onstop = null;
			recorder.addEventListener("stop", finalize, { once: true });
			stopRecording();
		} else {
			finalize();
		}
	}, [isRecording, stopRecording, uploadAudio, onClose]);

	const clearRecording = useCallback(() => {
		audioChunksRef.current = [];
		setRecordingTime(0);
		setIsRecording(false);
		if (intervalRef.current) clearInterval(intervalRef.current);
		if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
		console.log("Enregistrement supprimé");
	}, []);

	const visualize = (stream) => {
		const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
		const analyser = audioCtx.createAnalyser();
		const source = audioCtx.createMediaStreamSource(stream);
		source.connect(analyser);

		analyser.fftSize = 64;
		const bufferLength = analyser.fftSize;
		const dataArray = new Uint8Array(bufferLength);

		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d");

		const draw = () => {
			animationIdRef.current = requestAnimationFrame(draw);
			analyser.getByteTimeDomainData(dataArray);
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			const barWidth = canvas.width / bufferLength;
			const centerY = canvas.height / 2;
			const amplification = 3;

			for (let i = 0; i < bufferLength; i++) {
				const v = (dataArray[i] - 128) / 128;
				const barHeight = Math.abs(v) * (canvas.height / 2) * amplification;
				const x = i * barWidth;
				ctx.fillStyle = "red";
				ctx.fillRect(x, centerY - barHeight, barWidth - 2, barHeight);
				ctx.fillRect(x, centerY, barWidth - 2, barHeight);
			}
		};
		draw();
	};

	const formatTime = (time) => {
		const minutes = String(Math.floor(time / 60)).padStart(2, "0");
		const seconds = String(time % 60).padStart(2, "0");
		return `${minutes}:${seconds}`;
	};

	useEffect(() => {
		startRecording();
		return () => stopRecording();
	}, [startRecording, stopRecording]);

	return (
		<div>
			<canvas ref={canvasRef} style={{ display: "block", margin: "20px auto" }} />

			<p className="lead text-center">
				{formatTime(recordingTime)} / {formatTime(MAX_DURATION)}
			</p>

			<div className="text-center">
				<Button
					title={t("audioRecorderPopupDeleteRecordingBtn")}
					className="btn-link"
					onClick={clearRecording}
					disabled={recordingTime === 0}
				>
					<svg width="25" height="29" viewBox="0 0 25 29" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M4.67958 28.1408C3.84847 28.1408 3.13847 27.8465 2.54958 27.2579C1.96097 26.669 1.66667 25.959 1.66667 25.1279V3.97417H0V1.47417H7.5V0H17.5V1.47417H25V3.97417H23.3333V25.1279C23.3333 25.9699 23.0417 26.6825 22.4583 27.2658C21.875 27.8492 21.1624 28.1408 20.3204 28.1408H4.67958ZM20.8333 3.97417H4.16667V25.1279C4.16667 25.2776 4.21472 25.4006 4.31083 25.4967C4.40694 25.5928 4.52986 25.6408 4.67958 25.6408H20.3204C20.4487 25.6408 20.5662 25.5874 20.6729 25.4804C20.7799 25.3738 20.8333 25.2563 20.8333 25.1279V3.97417ZM8.17333 22.3075H10.6729V7.3075H8.17333V22.3075ZM14.3271 22.3075H16.8267V7.3075H14.3271V22.3075Z" fill="#DC0917"/>
					</svg>
				</Button>

				{!isRecording ? (
					<Button
						title={t("audioRecorderPopupStartRecordingBtn")}
						className="btn-link"
						onClick={startRecording}
					>
						<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								fill="#1C4CD3"
								d="M15.836 31.666q-3.285 0-6.176-1.246t-5.028-3.384c-2.138-2.138-2.554-3.101-3.385-5.026S0 18.025 0 15.836q0-3.285 1.247-6.176Q2.494 6.771 4.63 4.632c2.136-2.139 3.1-2.554 5.026-3.385S13.641 0 15.831 0s4.249.416 6.175 1.247 3.604 1.959 5.028 3.384 2.554 3.1 3.386 5.026q1.246 2.889 1.246 6.174c0 3.285-.415 4.249-1.246 6.175s-1.959 3.604-3.384 5.028-3.101 2.554-5.026 3.386q-2.89 1.246-6.174 1.246m-.003-2.5q5.583 0 9.458-3.875 3.876-3.873 3.875-9.458 0-5.584-3.875-9.458Q21.418 2.5 15.833 2.5c-5.585 0-6.875 1.292-9.458 3.875S2.5 12.111 2.5 15.833q0 5.583 3.875 9.458t9.458 3.875"
							/>
							<path fill="#1C4CD3" d="M12.75 9.75v12.5l8.5-6.25z"/>
						</svg>
					</Button>
				) : (
					<Button
						title={t("audioRecorderPopupStopRecordingBtn")}
						className="btn-link"
						onClick={stopRecording}
					>
						<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M11.5067 22.0833H14.0063V9.58333H11.5067V22.0833ZM17.6604 22.0833H20.16V9.58333H17.6604V22.0833ZM15.8362 31.6667C13.6462 31.6667 11.5878 31.2511 9.66083 30.42C7.73389 29.5889 6.05778 28.461 4.6325 27.0362C3.20722 25.6115 2.07875 23.9361 1.24708 22.01C0.415694 20.0839 0 18.026 0 15.8362C0 13.6462 0.415555 11.5878 1.24667 9.66083C2.07778 7.73389 3.20569 6.05778 4.63042 4.6325C6.05514 3.20722 7.73056 2.07875 9.65667 1.24708C11.5828 0.415694 13.6407 0 15.8304 0C18.0204 0 20.0789 0.415555 22.0058 1.24667C23.9328 2.07778 25.6089 3.20569 27.0342 4.63042C28.4594 6.05514 29.5879 7.73056 30.4196 9.65667C31.251 11.5828 31.6667 13.6407 31.6667 15.8304C31.6667 18.0204 31.2511 20.0789 30.42 22.0058C29.5889 23.9328 28.461 25.6089 27.0362 27.0342C25.6115 28.4594 23.9361 29.5879 22.01 30.4196C20.0839 31.251 18.026 31.6667 15.8362 31.6667ZM15.8333 29.1667C19.5556 29.1667 22.7083 27.875 25.2917 25.2917C27.875 22.7083 29.1667 19.5556 29.1667 15.8333C29.1667 12.1111 27.875 8.95833 25.2917 6.375C22.7083 3.79167 19.5556 2.5 15.8333 2.5C12.1111 2.5 8.95833 3.79167 6.375 6.375C3.79167 8.95833 2.5 12.1111 2.5 15.8333C2.5 19.5556 3.79167 22.7083 6.375 25.2917C8.95833 27.875 12.1111 29.1667 15.8333 29.1667Z"
								fill="#314D60"/>
						</svg>
					</Button>
				)}

				<Button
					title={t("audioRecorderPopupSaveRecordingBtn")}
					className="btn-link"
					onClick={saveRecording}
					disabled={recordingTime === 0 || recordSubmitted}
				>
					<svg width="36" height="26" viewBox="0 0 36 26" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M12.396 25.4347L0.478271 13.534L2.86652 11.1486L12.396 20.6645L32.8726 0.217285L35.2609 2.60266L12.396 25.4347Z"
							fill="#3E7846"/>
					</svg>
				</Button>
			</div>
		</div>
	)
}

export default AudioRecorder;
